import React, { JSX, useState, useEffect } from 'react';
import {
    X, Search, Users, Building2, Shield, UserX, Plus, Trash2, Check, AlertCircle
} from 'lucide-react';
import { apiService } from '../../../../components/api/apiService';
import { authservice } from '../../../../components/api/authservice';
import { governedMcpApi } from '../../governed-mcp-gateway/governedMcpApi';

const MASTERDATA_UPSERT_URL = '/api/MasterData/upsert';
const MASTERDATA_ASSIGNMENTS_PATH = '/api/MasterData';
const MASTERDATA_DEPARTMENTS_URL = '/api/MasterData/departments';
const PERMISSION_ID_STATIC = '42d997c1-6454-4f57-be38-e6144c7a81b4';

interface AssignmentsResponse {
    module_id: string;
    departments: string[];
    users: string[];
    roles: string[];
    exclusions: string[];
    departments_count?: number;
    users_count?: number;
    roles_count?: number;
    exclusions_count?: number;
}

// Interfaces
interface Department {
    id: string;
    name: string;
    description?: string;
    memberCount?: number;
}

interface User {
    id: string;
    name: string;
    email: string;
    avatar?: string;
    department?: string;
}

interface Role {
    id: string;
    name: string;
    description?: string;
    permissions?: string[];
}

interface AssignedPermissions {
    departments: Department[];
    users: User[];
    roles: Role[];
    exclusions: User[];
}

type MasterDataUpsertType = 'departments' | 'users' | 'roles' | 'exclusions';

function idDelta(before: string[] | undefined, after: string[]): string[] {
    const prev = new Set((before || []).map((id) => id.trim()).filter(Boolean));
    const next = new Set(after.map((id) => id.trim()).filter(Boolean));
    const changed: string[] = [];
    prev.forEach((id) => {
        if (!next.has(id)) changed.push(id);
    });
    next.forEach((id) => {
        if (!prev.has(id)) changed.push(id);
    });
    return changed;
}

function uniqueIds(ids: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    ids.forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        out.push(id);
    });
    return out;
}

interface PermissionsAssignmentModalProps {
    onClose: () => void;
    onSave: (permissions: AssignedPermissions) => void;
    entityType: 'knowledge-base' | 'entity' | 'resource' | 'llm-configuration' | 'connections' | 'vector-store' | 'chat-agent' | 'analytics_hub' | 'catalog';
    entityName: string;
    /** Only used when module_id is not set (legacy mode). When module_id is set, modal loads its own data. */
    initialPermissions?: AssignedPermissions;
    /** When true, render inline without overlay (e.g. embedded in Security page) */
    embedded?: boolean;
    /** Only used when module_id is not set (legacy mode). When module_id is set, modal fetches departments/users/roles and assignments itself. */
    availableDepartments?: Department[];
    availableUsers?: User[];
    availableRoles?: Role[];
    /** Module ID. When set, modal loads departments, users, roles and assignments from MasterData APIs and saves via upsert. */
    module_id?: string;
    /** Object ID (e.g. agent_id, master_id) for assignments GET and upsert. */
    object_id?: string;
}

type TabType = 'departments' | 'users' | 'roles' | 'exclusions';

// Mock data - Replace with actual API calls
const mockDepartments: Department[] = [ 
];

const mockUsers: User[] = [ 
];

const mockRoles: Role[] = [ 
];

const PermissionsAssignmentModal: React.FC<PermissionsAssignmentModalProps> = ({
    onClose,
    onSave,
    entityName,
    initialPermissions,
    embedded = false,
    availableDepartments: availableDepartmentsProp,
    availableUsers: availableUsersProp,
    availableRoles: availableRolesProp,
    module_id: moduleId,
    object_id: objectId
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('departments');
    const [searchQuery, setSearchQuery] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [assignmentsLoading, setAssignmentsLoading] = useState(false);
    const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
    const [fetchedAssignmentIds, setFetchedAssignmentIds] = useState<{
        departments: string[];
        users: string[];
        roles: string[];
        exclusions: string[];
    } | null>(null);

    const [fetchedDepartments, setFetchedDepartments] = useState<Department[]>([]);
    const [fetchedUsers, setFetchedUsers] = useState<User[]>([]);
    const [fetchedRoles, setFetchedRoles] = useState<Role[]>([]);
    const [listsLoading, setListsLoading] = useState(false);
    const [listsError, setListsError] = useState<string | null>(null);

    const [assignedDepartments, setAssignedDepartments] = useState<Department[]>(initialPermissions?.departments || []);
    const [assignedUsers, setAssignedUsers] = useState<User[]>(initialPermissions?.users || []);
    const [assignedRoles, setAssignedRoles] = useState<Role[]>(initialPermissions?.roles || []);
    const [excludedUsers, setExcludedUsers] = useState<User[]>(initialPermissions?.exclusions || []);

    // When module_id is set: fetch departments (MasterData), users and roles (authservice)
    useEffect(() => {
        if (!moduleId) return;
        let cancelled = false;
        setListsLoading(true);
        setListsError(null);
        Promise.all([
            apiService.get(MASTERDATA_DEPARTMENTS_URL, {}, 'backend'),
            authservice.getUsers(),
            authservice.getRoles()
        ])
            .then(([deptData, userData, roleData]: any[]) => {
                if (cancelled) return;
                const toDept = (d: { id: string; name: string }): Department => ({ id: d.id, name: d.name });
                const toUser = (u: { id: string; name?: string; email?: string }): User => ({
                    id: u.id,
                    name: u.name ?? u.email ?? u.id,
                    email: u.email ?? u.name ?? u.id
                });
                const toRole = (r: { roleId: string; roleName: string } | { id: string; name: string }): Role =>
                    'roleId' in r ? { id: r.roleId, name: r.roleName } : { id: r.id, name: r.name };
                const deptList = Array.isArray(deptData) ? deptData : deptData?.data ?? [];
                const userList = Array.isArray(userData) ? userData : userData?.data ?? [];
                const roleList = Array.isArray(roleData) ? roleData : roleData?.data ?? [];
                setFetchedDepartments(deptList.map(toDept));
                setFetchedUsers(userList.map(toUser));
                setFetchedRoles(roleList.map(toRole));
            })
            .catch((err: any) => {
                if (cancelled) return;
                setListsError(err?.response?.data?.message || err?.message || 'Failed to load lists.');
            })
            .finally(() => {
                if (!cancelled) setListsLoading(false);
            });
        return () => { cancelled = true; };
    }, [moduleId]);

    // Fetch assignments when module_id is provided
    useEffect(() => {
        if (!moduleId) return;
        let cancelled = false;
        setAssignmentsLoading(true);
        setAssignmentsError(null);
        const params = objectId ? { object_id: objectId } : {};
        apiService
            .get(`${MASTERDATA_ASSIGNMENTS_PATH}/${moduleId}/assignments`, params, 'backend')
            .then((data: any) => {
                if (cancelled) return;
                const body = data?.data ?? data;
                setFetchedAssignmentIds({
                    departments: Array.isArray(body?.departments) ? body.departments : [],
                    users: Array.isArray(body?.users) ? body.users : [],
                    roles: Array.isArray(body?.roles) ? body.roles : [],
                    exclusions: Array.isArray(body?.exclusions) ? body.exclusions : []
                });
            })
            .catch((err: any) => {
                if (cancelled) return;
                setAssignmentsError(err?.response?.data?.message || err?.message || 'Failed to load assignments.');
            })
            .finally(() => {
                if (!cancelled) setAssignmentsLoading(false);
            });
        return () => { cancelled = true; };
    }, [moduleId, objectId]);

    // Source lists: when module_id set use self-fetched; else use props or mock
    const departmentsSource = moduleId ? fetchedDepartments : (availableDepartmentsProp ?? mockDepartments);
    const usersSource = moduleId ? fetchedUsers : (availableUsersProp ?? mockUsers);
    const rolesSource = moduleId ? fetchedRoles : (availableRolesProp ?? mockRoles);

    // When we get new assignment IDs from the API, map them to full objects (only when fetchedAssignmentIds changes)
    useEffect(() => {
        if (!fetchedAssignmentIds) return;
        const mapDept = (id: string): Department => departmentsSource.find(d => d.id === id) ?? { id, name: id };
        const mapUser = (id: string): User => usersSource.find(u => u.id === id) ?? { id, name: id, email: id };
        const mapRole = (id: string): Role => rolesSource.find(r => r.id === id) ?? { id, name: id };
        setAssignedDepartments(fetchedAssignmentIds.departments.map(mapDept));
        setAssignedUsers(fetchedAssignmentIds.users.map(mapUser));
        setAssignedRoles(fetchedAssignmentIds.roles.map(mapRole));
        setExcludedUsers(fetchedAssignmentIds.exclusions.map(mapUser));
    }, [fetchedAssignmentIds, departmentsSource, usersSource, rolesSource]);

    // When source lists change, enrich existing assigned items (resolve names) without replacing the list
    useEffect(() => {
        setAssignedDepartments(prev => prev.length ? prev.map(d => departmentsSource.find(x => x.id === d.id) ?? d) : prev);
        setAssignedUsers(prev => prev.length ? prev.map(u => usersSource.find(x => x.id === u.id) ?? u) : prev);
        setAssignedRoles(prev => prev.length ? prev.map(r => rolesSource.find(x => x.id === r.id) ?? r) : prev);
        setExcludedUsers(prev => prev.length ? prev.map(u => usersSource.find(x => x.id === u.id) ?? u) : prev);
    }, [departmentsSource, usersSource, rolesSource]);

    const tabs: Array<{ id: TabType; label: string; icon: JSX.Element; count: number }> = [
        { id: 'departments', label: 'Departments', icon: <Building2 className="w-4 h-4" />, count: assignedDepartments.length },
        { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" />, count: assignedUsers.length },
        { id: 'roles', label: 'Roles', icon: <Shield className="w-4 h-4" />, count: assignedRoles.length },
        { id: 'exclusions', label: 'Exclusions', icon: <UserX className="w-4 h-4" />, count: excludedUsers.length },
    ];

    const permissionsPayload = {
        departments: assignedDepartments,
        users: assignedUsers,
        roles: assignedRoles,
        exclusions: excludedUsers
    };

    const handleSave = async () => {
        setSaveMessage(null);
        if (moduleId) {
            setSaving(true);
            const types: MasterDataUpsertType[] = ['departments', 'users', 'roles', 'exclusions'];
            const idsByType: Record<MasterDataUpsertType, string[]> = {
                departments: assignedDepartments.map(d => d.id),
                users: assignedUsers.map(u => u.id),
                roles: assignedRoles.map(r => r.id),
                exclusions: excludedUsers.map(u => u.id)
            };
            try {
                let lastMessage = 'Module assignments updated successfully.';
                for (const type of types) { 
                    const res = await apiService.post(
                        MASTERDATA_UPSERT_URL,
                        {
                            type,
                            permission_id: PERMISSION_ID_STATIC,
                            module_id: moduleId,
                            ...(objectId && { object_id: objectId }),
                            ids_list: idsByType[type]
                        },
                        'backend'
                    );
                    if (res && typeof (res as any).message === 'string') lastMessage = (res as any).message;
                }
                const prev = fetchedAssignmentIds;
                await governedMcpApi.invalidateGrants({
                    module_id: moduleId,
                    users: uniqueIds([
                        ...idDelta(prev?.users, idsByType.users),
                        ...idDelta(prev?.exclusions, idsByType.exclusions),
                    ]),
                    roles: idDelta(prev?.roles, idsByType.roles),
                    departments: idDelta(prev?.departments, idsByType.departments),
                });
                setSaveMessage({ type: 'success', text: lastMessage });
                // Keep fetchedAssignmentIds in sync so the map effect (when it re-runs on parent re-render) doesn't overwrite with stale data
                setFetchedAssignmentIds({
                    departments: assignedDepartments.map(d => d.id),
                    users: assignedUsers.map(u => u.id),
                    roles: assignedRoles.map(r => r.id),
                    exclusions: excludedUsers.map(u => u.id)
                });
                onSave(permissionsPayload);
                setTimeout(() => setSaveMessage(null), 4000);
                if (!embedded) onClose();
            } catch (err: any) {
                const errorText = err?.response?.data?.message || err?.message || 'Failed to save permissions.';
                setSaveMessage({ type: 'error', text: errorText });
            } finally {
                setSaving(false);
            }
        } else {
            onSave(permissionsPayload);
            if (!embedded) onClose();
        }
    };

    const isItemAssigned = (id: string, type: TabType): boolean => {
        switch (type) {
            case 'departments': return assignedDepartments.some(d => d.id === id);
            case 'users': return assignedUsers.some(u => u.id === id);
            case 'roles': return assignedRoles.some(r => r.id === id);
            case 'exclusions': return excludedUsers.some(u => u.id === id);
            default: return false;
        }
    };

    const toggleDepartment = (dept: Department) => {
        setAssignedDepartments(prev =>
            prev.some(d => d.id === dept.id) ? prev.filter(d => d.id !== dept.id) : [...prev, dept]
        );
    };

    const toggleUser = (user: User, isExclusion: boolean) => {
        if (isExclusion) {
            setExcludedUsers(prev =>
                prev.some(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]
            );
        } else {
            setAssignedUsers(prev =>
                prev.some(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]
            );
        }
    };

    const toggleRole = (role: Role) => {
        setAssignedRoles(prev =>
            prev.some(r => r.id === role.id) ? prev.filter(r => r.id !== role.id) : [...prev, role]
        );
    };

    const removeDepartment = (id: string) => {
        setAssignedDepartments(prev => prev.filter(d => d.id !== id));
    };

    const removeUser = (id: string, isExclusion: boolean) => {
        if (isExclusion) {
            setExcludedUsers(prev => prev.filter(u => u.id !== id));
        } else {
            setAssignedUsers(prev => prev.filter(u => u.id !== id));
        }
    };

    const removeRole = (id: string) => {
        setAssignedRoles(prev => prev.filter(r => r.id !== id));
    };

    const getFilteredDepartments = (): Department[] => {
        const query = searchQuery.toLowerCase();
        return departmentsSource.filter(d => 
            d.name.toLowerCase().includes(query) || 
            (d.description && d.description.toLowerCase().includes(query))
        );
    };

    const getFilteredUsers = (): User[] => {
        const query = searchQuery.toLowerCase();
        return usersSource.filter(u => 
            u.name.toLowerCase().includes(query) || 
            u.email.toLowerCase().includes(query) ||
            (u.department && u.department.toLowerCase().includes(query))
        );
    };

    const getFilteredRoles = (): Role[] => {
        const query = searchQuery.toLowerCase();
        return rolesSource.filter(r => 
            r.name.toLowerCase().includes(query) || 
            (r.description && r.description.toLowerCase().includes(query))
        );
    };

    const renderDepartmentCard = (dept: Department, isAssigned: boolean) => (
        <button
            key={dept.id}
            onClick={() => toggleDepartment(dept)}
            className={`group relative bg-neutral-800 rounded-lg p-3 text-left transition-all hover:scale-102 border-2 w-full ${
                isAssigned ? 'border-indigo-500 bg-indigo-950/20' : 'border-transparent hover:border-indigo-500/30'
            }`}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-indigo-400" />
                        <h4 className="text-white font-semibold text-sm">{dept.name}</h4>
                    </div>
                    <p className="text-gray-400 text-xs mb-2">{dept.description}</p>
                    <div className="text-gray-500 text-xs">
                        <Users className="w-3 h-3 inline mr-1" />
                        {dept.memberCount} members
                    </div>
                </div>
                {isAssigned && (
                    <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                    </div>
                )}
            </div>
        </button>
    );

    const renderUserCard = (user: User, isAssigned: boolean) => (
        <button
            key={user.id}
            onClick={() => toggleUser(user, activeTab === 'exclusions')}
            className={`group relative bg-neutral-800 rounded-lg p-3 text-left transition-all hover:scale-102 border-2 w-full ${
                isAssigned ? 'border-indigo-500 bg-indigo-950/20' : 'border-transparent hover:border-indigo-500/30'
            }`}
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1">
                    <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-semibold">
                            {user.name.split(' ').map(n => n[0]).join('')}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="text-white font-semibold text-sm truncate">{user.name}</h4>
                        <p className="text-gray-400 text-xs truncate">{user.email}</p>
                        {user.department && (
                            <div className="text-gray-500 text-xs mt-0.5">
                                <Building2 className="w-3 h-3 inline mr-1" />
                                {user.department}
                            </div>
                        )}
                    </div>
                </div>
                {isAssigned && (
                    <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                    </div>
                )}
            </div>
        </button>
    );

    const renderRoleCard = (role: Role, isAssigned: boolean) => (
        <button
            key={role.id}
            onClick={() => toggleRole(role)}
            className={`group relative bg-neutral-800 rounded-lg p-3 text-left transition-all hover:scale-102 border-2 w-full ${
                isAssigned ? 'border-indigo-500 bg-indigo-950/20' : 'border-transparent hover:border-indigo-500/30'
            }`}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <Shield className="w-4 h-4 text-indigo-400" />
                        <h4 className="text-white font-semibold text-sm">{role.name}</h4>
                    </div>
                    <p className="text-gray-400 text-xs mb-2">{role.description}</p>
                    {role.permissions && role.permissions.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {role.permissions.slice(0, 3).map(perm => (
                                <span key={perm} className="bg-neutral-700 text-gray-300 px-2 py-0.5 rounded text-xs">
                                    {perm}
                                </span>
                            ))}
                            {role.permissions.length > 3 && (
                                <span className="text-gray-500 text-xs">+{role.permissions.length - 3}</span>
                            )}
                        </div>
                    )}
                </div>
                {isAssigned && (
                    <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                    </div>
                )}
            </div>
        </button>
    );

    const currentTab = tabs.find(t => t.id === activeTab);
    const isExclusionTab = activeTab === 'exclusions';
    const isUserTab = activeTab === 'users' || activeTab === 'exclusions';

    const content = (
            <div className={`portal-content bg-neutral-900 rounded-lg w-full overflow-hidden border border-neutral-800 shadow-2xl flex flex-col ${embedded ? 'max-h-full' : 'max-w-5xl max-h-[90vh]'}`}>
                {/* Header */}
                <div className="bg-neutral-950 border-b border-neutral-800 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-white">Manage Permissions</h2>
                            <p className="text-gray-400 text-xs mt-1">
                                Configure access for <span className="text-indigo-400">{entityName}</span>
                            </p>
                        </div>
                        {!embedded && (
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-neutral-800 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-neutral-900 border-b border-neutral-800 px-4 py-3">
                    <div className="flex gap-2">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-xs ${
                                    activeTab === tab.id
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-neutral-800 text-gray-300 hover:bg-neutral-700'
                                }`}
                            >
                                {tab.icon}
                                <span className="font-medium">{tab.label}</span>
                                {tab.count > 0 && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                                        activeTab === tab.id ? 'bg-indigo-700' : 'bg-neutral-700'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Info Banner */}
                {isExclusionTab && (
                    <div className="bg-amber-950/30 border-b border-amber-900/50 px-4 py-2 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-amber-200 text-xs">
                            Users in exclusions will be denied access even if they belong to an assigned department, role, or user group.
                        </p>
                    </div>
                )}

                {/* Load errors (when module_id set: lists or assignments) */}
                {(assignmentsError || listsError) && (
                    <div className="bg-red-950/50 border-b border-red-900/50 px-4 py-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <p className="text-red-200 text-sm">{assignmentsError || listsError}</p>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-hidden flex">
                    {(assignmentsLoading || (moduleId && listsLoading)) ? (
                        <div className="flex-1 flex items-center justify-center p-8">
                            <div className="text-center text-gray-400">
                                <span className="inline-block w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
                                <p className="text-sm">{moduleId ? 'Loading permissions...' : 'Loading assignments...'}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                    {/* Available Items */}
                    <div className="flex-1 overflow-y-auto p-4 border-r border-neutral-800">
                        <div className="mb-3">
                            <label className="text-gray-400 text-xs font-medium mb-2 block">
                                Available {currentTab?.label}
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={`Search ${currentTab?.label.toLowerCase()}...`}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-neutral-800 border border-neutral-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            {activeTab === 'departments' && getFilteredDepartments().map(dept => 
                                renderDepartmentCard(dept, isItemAssigned(dept.id, 'departments'))
                            )}
                            {isUserTab && getFilteredUsers().map(user => 
                                renderUserCard(user, isItemAssigned(user.id, activeTab))
                            )}
                            {activeTab === 'roles' && getFilteredRoles().map(role => 
                                renderRoleCard(role, isItemAssigned(role.id, 'roles'))
                            )}
                        </div>
                    </div>

                    {/* Assigned Items */}
                    <div className="w-80 overflow-y-auto p-4 bg-neutral-950">
                        <div className="mb-3">
                            <label className="text-gray-400 text-xs font-medium mb-2 block">
                                Assigned (
                                {activeTab === 'departments' && assignedDepartments.length}
                                {activeTab === 'users' && assignedUsers.length}
                                {activeTab === 'roles' && assignedRoles.length}
                                {activeTab === 'exclusions' && excludedUsers.length}
                                )
                            </label>
                        </div>

                        {((activeTab === 'departments' && assignedDepartments.length === 0) ||
                          (activeTab === 'users' && assignedUsers.length === 0) ||
                          (activeTab === 'roles' && assignedRoles.length === 0) ||
                          (activeTab === 'exclusions' && excludedUsers.length === 0)) ? (
                            <div className="text-center py-8">
                                <Plus className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400 text-sm">No items assigned</p>
                                <p className="text-gray-500 text-xs mt-1">
                                    Select items to grant access
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {activeTab === 'departments' && assignedDepartments.map(dept => (
                                    <div key={dept.id} className="bg-neutral-800 rounded-lg p-2 border border-indigo-500/30 flex items-center justify-between group">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm font-medium truncate">{dept.name}</p>
                                            <p className="text-gray-400 text-xs truncate">{dept.memberCount} members</p>
                                        </div>
                                        <button
                                            onClick={() => removeDepartment(dept.id)}
                                            className="ml-2 text-gray-400 hover:text-red-400 transition-colors p-1 hover:bg-neutral-700 rounded flex-shrink-0"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                
                                {activeTab === 'users' && assignedUsers.map(user => (
                                    <div key={user.id} className="bg-neutral-800 rounded-lg p-2 border border-indigo-500/30 flex items-center justify-between group">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm font-medium truncate">{user.name}</p>
                                            <p className="text-gray-400 text-xs truncate">{user.email}</p>
                                        </div>
                                        <button
                                            onClick={() => removeUser(user.id, false)}
                                            className="ml-2 text-gray-400 hover:text-red-400 transition-colors p-1 hover:bg-neutral-700 rounded flex-shrink-0"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}

                                {activeTab === 'roles' && assignedRoles.map(role => (
                                    <div key={role.id} className="bg-neutral-800 rounded-lg p-2 border border-indigo-500/30 flex items-center justify-between group">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm font-medium truncate">{role.name}</p>
                                            <p className="text-gray-400 text-xs truncate">{role.description}</p>
                                        </div>
                                        <button
                                            onClick={() => removeRole(role.id)}
                                            className="ml-2 text-gray-400 hover:text-red-400 transition-colors p-1 hover:bg-neutral-700 rounded flex-shrink-0"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}

                                {activeTab === 'exclusions' && excludedUsers.map(user => (
                                    <div key={user.id} className="bg-neutral-800 rounded-lg p-2 border border-indigo-500/30 flex items-center justify-between group">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm font-medium truncate">{user.name}</p>
                                            <p className="text-gray-400 text-xs truncate">{user.email}</p>
                                        </div>
                                        <button
                                            onClick={() => removeUser(user.id, true)}
                                            className="ml-2 text-gray-400 hover:text-red-400 transition-colors p-1 hover:bg-neutral-700 rounded flex-shrink-0"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-neutral-950 border-t border-neutral-800 p-4 flex flex-col gap-3">
                    {saveMessage && (
                        <div
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                                saveMessage.type === 'success'
                                    ? 'bg-emerald-950/50 border border-emerald-800 text-emerald-200'
                                    : 'bg-red-950/50 border border-red-800 text-red-200'
                            }`}
                        >
                            {saveMessage.type === 'success' ? (
                                <Check className="w-4 h-4 flex-shrink-0 text-emerald-400" />
                            ) : (
                                <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-400" />
                            )}
                            <span>{saveMessage.text}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-400">
                            {assignedDepartments.length > 0 && (
                                <span className="mr-3">
                                    <Building2 className="w-3 h-3 inline mr-1" />
                                    {assignedDepartments.length} dept(s)
                                </span>
                            )}
                            {assignedUsers.length > 0 && (
                                <span className="mr-3">
                                    <Users className="w-3 h-3 inline mr-1" />
                                    {assignedUsers.length} user(s)
                                </span>
                            )}
                            {assignedRoles.length > 0 && (
                                <span className="mr-3">
                                    <Shield className="w-3 h-3 inline mr-1" />
                                    {assignedRoles.length} role(s)
                                </span>
                            )}
                            {excludedUsers.length > 0 && (
                                <span className="text-amber-400">
                                    <UserX className="w-3 h-3 inline mr-1" />
                                    {excludedUsers.length} exclusion(s)
                                </span>
                            )}
                        </div>
                        <div className="flex gap-3">
                            {!embedded && (
                                <button
                                    onClick={onClose}
                                    disabled={saving}
                                    className="text-gray-400 hover:text-white px-4 py-2 rounded-lg hover:bg-neutral-800 transition-colors text-sm disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 disabled:cursor-not-allowed px-5 py-2 rounded-lg text-white transition-colors text-sm font-medium flex items-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Permissions'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
    );

    if (embedded) {
        return content;
    }
    return (
        <div className="portal-content fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            {content}
        </div>
    );
};

export default PermissionsAssignmentModal;